// The backoffice theme (tokens.md §5): the legacy Quasar CSS variables, light and dark, keyed by their name without
// `--`, plus the metrics the legacy SCSS pins (rows, fields, radii, shell). test/backoffice-theme.test.ts checks every
// row of tokens.md §5. A token legacy leaves undefined in dark mode keeps its light value (the variable is not
// overridden under `body--dark`), which is what a legacy screen shows.

export interface BackofficeColors {
  readonly 'q-primary': string;
  readonly 'q-secondary': string;
  readonly 'q-accent': string;
  readonly 'q-positive': string;
  readonly 'q-negative': string;
  readonly 'q-info': string;
  readonly 'q-warning': string;
  readonly 'admin-topbar-red': string;
  readonly 'surface-lower': string;
  /** An outlined field's own fill: white in light, legacy's `--q-dark-surface-12dp` in dark (_dark.scss `.q-field--outlined .q-field__control::before`). */
  readonly 'field-surface': string;
  /** A dialog's card (q-card in a q-dialog): white in light, legacy's `--q-dark` #212224 in dark (Quasar's q-card--dark; B618). */
  readonly 'dialog-surface': string;
  /**
   * A q-menu's own fill (B1287): white in light, legacy's `--q-dark-surface-06dp` #2a2b2e in dark, where legacy also drops
   * the menu's shadow (_dark.scss `.q-menu, .ui-location-menu`). A q-date in one is 06dp too.
   */
  readonly 'menu-surface': string;
  /** A q-card on a page (UiCard, UiBorderedCard, UiServiceInfoCard; B1299): white in light, Quasar's q-card--dark `--q-dark` #212224 in dark. */
  readonly 'card-surface': string;
  /** A q-item's ink in a plain q-menu (B1287): the body's --q-text-secondary in light, Quasar's q-item--dark #fff in dark. */
  readonly 'menu-ink': string;
  /** A q-item's ink in a `rad-select-dropdown` menu (B1287): --q-text-primary in light, q-item--dark's #fff in dark. */
  readonly 'menu-ink-body': string;
  /**
   * A q-item hovered in a q-menu (B1287): gray-50 in light, as before; in dark Quasar's focus helper (the item's #fff at 15 %,
   * itself black 10 % and white 40 %) over 06dp, measured rgb(72, 73, 75) on legacy's staff Accounts menus.
   */
  readonly 'menu-item-hover': string;
  readonly 'sidebar-background': string;
  readonly 'sidebar-menu-hover': string;
  readonly 'q-brand-primary': string;
  readonly 'q-brand-secondary': string;
  readonly 'q-brand-gray-dark': string;
  readonly 'q-brand-gray-alternate': string;
  readonly 'q-brand-primary-alternate': string;
  readonly 'q-brand-secondary-alternate': string;
  readonly 'q-rad-medium': string;
  readonly 'q-rad-dark': string;
  readonly 'q-rad-alternate': string;
  readonly 'q-rad-alternate-light': string;
  readonly 'q-red-bg-select': string;
  readonly 'q-rad-secondary': string;
  readonly 'q-rad-accent': string;
  readonly 'q-text-primary': string;
  readonly 'q-text-secondary': string;
  readonly 'q-text-tertiary': string;
  /**
   * A dialog's close glyph (legacy `.ui-card-close-button`, B638): --q-text-tertiary #8c8d8c in light and $whiteHigh in
   * dark. Kept at legacy's grey under the AA contrast default (`backofficeAaLight` leaves it out): it is a non-text
   * control, which needs 3:1, and #8c8d8c has 3.1:1 on gray-50 #f5f5f5.
   */
  readonly 'close-glyph': string;
  /**
   * Account User Invite's Invite button (legacy InviteUser.vue BaseButton `color="purple"`, B1322): `$purple` #ad48e0 with
   * white in light; legacy's dark BaseButton draws it #c59afe (--q-brand-purple) with black. Measured on GG LA at 1024.
   */
  readonly 'purple-button': string;
  readonly 'purple-button-ink': string;
  /**
   * A location's Invite New User button (legacy LocationDetail BaseButton `color="orange"`, B1337): `$orange` #f79206 with
   * white in light; legacy's dark BaseButton draws it $orangeDark #ffd497 with black. Measured on GG Sacramento at 1600.
   */
  readonly 'orange-button': string;
  readonly 'orange-button-ink': string;
  /**
   * ID Verification's age chips (legacy IdVerificationsTable.vue, B1645): 21 in `royal-blue` #326ae4 and 18 in
   * `tennessee-orange` #f57c00, each on a tenth of its colour, in light and dark alike. Both inks fail AA on their tint in
   * light (4.2:1 and 2.45:1), so the AA contrast default darkens only the inks (L143).
   */
  /**
   * The schedule's row avatars (legacy `avatarVariantFor`, library/schedule.ts, on UiColorBox): white 12 px initials on a
   * tone hashed from the row id, purple, orange, green, blue or dark grey, in both the week grid and the Day view. In
   * light, white fails AA on the first three (4.1:1, 2.4:1, 3.2:1), so the AA contrast default darkens those fills in the
   * same hues (L143); dark keeps legacy's (#1c1d20 initials, 7.6:1 and up).
   */
  readonly 'avatar-purple': string;
  readonly 'avatar-orange': string;
  readonly 'avatar-green': string;
  readonly 'avatar-blue': string;
  readonly 'avatar-gray': string;
  readonly 'age-chip-blue': string;
  readonly 'age-chip-blue-ink': string;
  readonly 'age-chip-orange': string;
  readonly 'age-chip-orange-ink': string;
  /**
   * The order sheet's PAYMENT VOID banner (legacy DrawerOrderPopup `tw-bg-tennessee-orange/10 tw-text-tennessee-orange`,
   * B1621): `tennessee-orange` #f57c00, its band a tenth of it, in light and dark alike. The ink is 2.45:1 on the band in
   * light, so the AA contrast default darkens only the ink (L143).
   */
  readonly 'void-banner-orange': string;
  readonly 'void-banner-orange-ink': string;
  /**
   * Legacy's brand orange as text on its own tenth (`--q-brand-orange` on `--q-brand-orange-lighter`): the request status
   * chip's PENDING and other orange statuses, the schedule's Time off markers, Availability's partial days and the order
   * sheet's VOIDED chip. It is about 2.2:1 in light (2.0:1 on the week grid's hatched day), so the AA contrast default
   * darkens it in the same hue to clear 4.5:1 on both (B1664, L143).
   */
  readonly 'orange-tint-ink': string;
  /**
   * The staff Account Detail's count avatars (legacy AccountUsers.vue / AccountLocations.vue q-avatar, B1337): their
   * fills are `purple-button` and `orange-button`, and their numbers white in light and `very-dark` #1c232e in dark
   * (`:text-color="appStore.isDarkMode ? 'very-dark' : 'white'"`).
   */
  readonly 'count-ink': string;
  readonly 'q-gray-25': string;
  readonly 'q-gray-50': string;
  readonly 'q-gray-75': string;
  /** Quasar's grey-6 and grey-7 (`text-grey-6`, `color="grey-6"`), which legacy pages use as is in both themes. */
  readonly 'q-grey-6': string;
  readonly 'q-grey-7': string;
  readonly 'q-border': string;
  readonly 'q-icon-primary': string;
  readonly 'q-brand-orange': string;
  readonly 'q-brand-orange-lighter': string;
  readonly 'q-brand-orange-alternate': string;
  readonly 'q-brand-orange-alternate-lighter': string;
  readonly 'q-brand-cyan': string;
  readonly 'q-brand-cyan-lighter': string;
  readonly 'q-brand-gold': string;
  readonly 'q-brand-gold-lighter': string;
  readonly 'q-brand-purple': string;
  readonly 'q-brand-purple-lighter': string;
  readonly 'q-brand-green': string;
  readonly 'q-brand-green-lighter': string;
  readonly 'q-brand-blue': string;
  readonly 'q-brand-blue-lighter': string;
  readonly 'q-brand-drawer-error': string;
  readonly 'q-brand-drawer-error-icon': string;
  readonly 'q-brand-sidebar-active-indicator': string;
  readonly 'q-surface-lift': string;
  readonly 'q-surface-toggle-background': string;
  readonly 'q-text-inverted': string;
  readonly 'q-text-high-emph': string;
  readonly 'q-text-med-emph': string;
  readonly 'q-text-low-emph': string;
  readonly 'q-light-yellow': string;
  readonly 'q-negative-light': string;
  readonly 'q-positive-light': string;
  /** BaseAlert's info fill (`.pp-alert-info`): --q-brand-primary-alternate in light, --q-brand-highlight-blue #414241 in dark (B677). */
  readonly 'alert-info-surface': string;
  /** BaseAlert `error-outline`'s fill (`.pp-alert-error-outline-soft`): the SCSS constant $redLight, so the same in dark (B682). */
  readonly 'alert-error-soft-surface': string;
  /**
   * The soft error alert's text, glyph and edge: --q-negative in light; in dark the light #bf1a2f too, because legacy's
   * dark --q-negative #f19b9b on the constant $redLight is 1.9:1 (legacy bug B691, fixed: ledger L787).
   */
  readonly 'alert-error-soft-ink': string;
  /** The filled error alert's text and glyph: white at 87 % on --q-negative in light; dark ink on legacy's dark #f19b9b, where its white was 1.9:1 (B691). */
  readonly 'on-negative': string;
  /**
   * BaseAlert `warning`'s fill (`.pp-alert-warning`, --q-warning): #efbd36 in light. Legacy's dark --q-warning is the
   * translucent #f2c03787, which no ink reads on over every surface, so dark takes the colour it draws over a dialog
   * (#212224), opaque (B690).
   */
  readonly 'alert-warning-surface': string;
  /** The warning alert's text and glyph: legacy's white on #efbd36 is 1.6:1 (legacy bug B696), so dark ink in both themes, 4.5:1 or more. */
  readonly 'on-warning': string;
  /**
   * BaseAlert `warning` outline's text and glyph (`.pp-alert-warning-outline`: --q-warning on no fill, edged in --q-warning):
   * legacy's #efbd36 in light and its translucent #f2c03787 in dark. Under the AA default the light ink is darkened in the
   * same hue to 5.0:1 (#efbd36 on white is 1.7:1).
   */
  readonly 'alert-warning-outline-ink': string;
  /** Table chip indicator (TableChipIndicator): active / neutral / negative backgrounds. */
  readonly 'chip-active-bg': string;
  readonly 'chip-neutral-bg': string;
  readonly 'chip-negative-bg': string;
  /** Table row hover overlay (light mode only in legacy). */
  readonly 'table-row-hover': string;
  /** A phone table's sticky first header cell (legacy _ui.scss white; _dark.scss --q-dark-surface-06dp; B1180). */
  readonly 'table-sticky-head': string;
  /** A phone table's sticky first body and summary cells (legacy #fafbfc; dark --q-dark-surface-06dp; B1180, B1206). */
  readonly 'table-sticky-cell': string;
  /** The item editor's empty image box (legacy ProductImageUpload .image-thumb: --q-gray-50; dark --q-dark-surface-24dp; B1156). */
  readonly 'image-thumb-surface': string;
  /** A Timecards card (legacy TimecardCards.vue's q-card `.timecard-card`): white in light; `--q-dark-surface-04dp` in dark (B1958). */
  readonly 'timecard-card-surface': string;
  /** A plain q-chip's fill (Quasar's `.q-chip` #e0e0e0); `--q-dark` #212224 in dark, Quasar's q-chip--dark (B1958). */
  readonly 'chip-plain-surface': string;
  /** A q-tab-panels' own fill (Quasar's `.q-tab-panels` white; `q-tab-panels--dark` `--q-dark` #212224), e.g. an employee's tabs (B1971). */
  readonly 'tab-panels-surface': string;
  /**
   * The schedule's week and day grids (legacy ScheduleCalendar.vue `--schedule-*`, ScheduleDayView.vue `--day-*`; B1969):
   * the name and day cells (white; dark `--q-dark-bg-one`), the head row and a day-view hour's hover (`--q-gray-50`; dark
   * `--q-dark-bg`), a head cell's hover and the totals row (`--q-gray-75`; dark `--q-dark-surface-12dp`), and the hatch
   * of a blocked day (black 5 %; dark white 6 %).
   */
  readonly 'schedule-surface': string;
  readonly 'schedule-head': string;
  readonly 'schedule-total': string;
  readonly 'schedule-stripe': string;
  /** The day view's midnight rule (legacy `--day-midnight-line`: black 24 %; dark white 28 %; B1969). */
  readonly 'schedule-midnight': string;
  /** The page behind everything (dark: `--q-dark-page`). */
  readonly 'page-background': string;
  /** Primary-green text on the light green tints (ACTIVE chips, "Complete", selected segments). */
  readonly 'positive-ink': string;
  /** Legacy's green status chip ink on #e4f3ea (Timecards' Approved and the like). */
  readonly 'chip-success-ink': string;
  /** Legacy's blue chip ink on #eff3fd (Timecards' Edited). */
  readonly 'chip-info-ink': string;
  /** The timecard editor's Time Worked card on #eaf5ee: its "on the clock" and its range line. */
  readonly 'time-worked-note-ink': string;
  readonly 'time-worked-line-ink': string;
  /** A grey note on #f3f4f6 (the timecard editor's empty breaks card). */
  readonly 'empty-note-ink': string;
  /** A dense tab bar's unselected labels (legacy q-tabs `text-grey`, the brand grey #9da0a0, in light and dark alike: B995). */
  readonly 'tab-quiet-ink': string;
  /** Legacy's `text-grey` ($grey, the brand grey #9da0a0, in light and dark alike): a customer's contact line, captions and stat labels (B999). */
  readonly 'q-grey': string;
  /** The invite dialog's line under Role (legacy tw-text-gray-400, #9ca3af, in light and dark alike), on white. */
  readonly 'invite-hint-ink': string;
  /** POS Devices' Register chip text (legacy POSDevices.vue `--q-brand-green` on `--q-brand-primary-alternate`, B1483). */
  readonly 'device-chip-ink': string;
  /** POS Devices' Handheld / KDS chip text (legacy `--q-brand-blue` on `--q-brand-blue-lighter`, B1483). */
  readonly 'device-chip-info-ink': string;
  /** A schedule card's paid-break coffee and count (legacy ShiftCard.vue `--q-brand-green`, on the card's 16 % tint; B1602). */
  readonly 'shift-break-paid-ink': string;
  /** A schedule card's unpaid-break coffee and count (legacy ShiftCard.vue `--q-brand-orange`; B1602). */
  readonly 'shift-break-unpaid-ink': string;
  /**
   * Voice Ordering's help lines (legacy StoreVoiceOrdering.vue `__help`: var(--q-border), #cccdcb in light, 1.6:1 on white;
   * B1162). In dark legacy's #373739 on the page is 1.5:1 and dark has no contrast switch, so it keeps the tertiary ink.
   */
  readonly 'voice-help-ink': string;
  /**
   * The time picker's panel (legacy TimeInput over @jobinsjp/vue3-datepicker): the library's own colours in light mode,
   * legacy `_dark.scss`'s overrides in dark (04dp panel, q-border rules, white-high text, primary for the chosen item).
   */
  readonly 'time-picker-background': string;
  readonly 'time-picker-border': string;
  readonly 'time-picker-text': string;
  readonly 'time-picker-text-hover': string;
  readonly 'time-picker-hover': string;
  readonly 'time-picker-active': string;
  /** The LOGGED IN AS ADMIN/RESELLER bar (legacy AppHeader `.ui-reseller-notification`: $redLight / $red, dark $red87 / $redLight). */
  readonly 'admin-topbar-background': string;
  readonly 'admin-topbar-ink': string;
}

export const backofficeLight: BackofficeColors = {
  'q-primary': '#4d833d',
  'q-secondary': '#326ae4',
  'q-accent': '#1f1f1f',
  'q-positive': '#4d833d',
  'q-negative': '#bf1a2f',
  'q-info': '#2599a8',
  'q-warning': '#efbd36',
  'admin-topbar-red': '#f4261b',
  'surface-lower': '#fff',
  'field-surface': '#fff',
  'dialog-surface': '#fff',
  'menu-surface': '#fff',
  'card-surface': '#fff',
  'menu-ink': '#646866',
  'menu-ink-body': '#1f1f1f',
  'menu-item-hover': '#f5f5f5',
  'sidebar-background': '#062626',
  'sidebar-menu-hover': '#cddfc9',
  'q-brand-primary': '#4d833d',
  'q-brand-secondary': '#326ae4',
  'q-brand-gray-dark': '#8c8d8c',
  'q-brand-gray-alternate': '#f4f4f4',
  'q-brand-primary-alternate': '#e8f1e4',
  'q-brand-secondary-alternate': '#eff3fd',
  'q-rad-medium': '#a3294f',
  'q-rad-dark': '#621830',
  'q-rad-alternate': '#cfd4ce',
  'q-rad-alternate-light': '#c0a3ac33',
  'q-red-bg-select': '#fdf1f1',
  'q-rad-secondary': '#cbe6c5',
  'q-rad-accent': '#ddcbd0',
  'q-text-primary': '#1f1f1f',
  'q-text-secondary': '#646866',
  'q-text-tertiary': '#8c8d8c',
  'close-glyph': '#8c8d8c',
  'purple-button': '#ad48e0',
  'purple-button-ink': '#ffffff',
  'orange-button': '#f79206',
  'orange-button-ink': '#ffffff',
  'avatar-purple': '#ab59d5',
  'avatar-orange': '#f49005',
  'avatar-green': '#679f55',
  'avatar-blue': '#326ae4',
  'avatar-gray': '#646866',
  'age-chip-blue': '#326ae4',
  'age-chip-blue-ink': '#326ae4',
  'age-chip-orange': '#f57c00',
  'age-chip-orange-ink': '#f57c00',
  'orange-tint-ink': '#f49005',
  'void-banner-orange': '#f57c00',
  'void-banner-orange-ink': '#f57c00',
  'count-ink': '#ffffff',
  'q-gray-25': '#fafafa',
  'q-gray-50': '#f5f5f5',
  'q-gray-75': '#efefef',
  'q-grey-6': '#9e9e9e',
  'q-grey-7': '#757575',
  'q-border': '#cccdcb',
  'q-icon-primary': '#a6bcee',
  'q-brand-orange': '#f49005',
  'q-brand-orange-lighter': '#f490051a',
  'q-brand-orange-alternate': '#ef8f07',
  'q-brand-orange-alternate-lighter': '#fdf2e1',
  'q-brand-cyan': '#26c6da',
  'q-brand-cyan-lighter': '#26c6da1a',
  'q-brand-gold': '#dcb332',
  'q-brand-gold-lighter': '#dcb3321a',
  'q-brand-purple': '#ab59d5',
  'q-brand-purple-lighter': '#ab59d51a',
  'q-brand-green': '#679f55',
  'q-brand-green-lighter': '#e8f1e4',
  'q-brand-blue': '#326ae4',
  'q-brand-blue-lighter': '#eff3fd',
  'q-brand-drawer-error': '#feefe0',
  'q-brand-drawer-error-icon': '#f57c00',
  'q-brand-sidebar-active-indicator': '#9dc290',
  'q-surface-lift': '#f5f5f5',
  'q-surface-toggle-background': '#efefef',
  'q-text-inverted': '#fff',
  'q-text-high-emph': '#ffffffde',
  'q-text-med-emph': '#ffffffb3',
  'q-text-low-emph': '#ffffff66',
  'q-light-yellow': '#fffbf6',
  'q-negative-light': '#ffdada',
  'q-positive-light': '#e8f1e4',
  'alert-info-surface': '#e8f1e4',
  'alert-error-soft-surface': '#ffeded',
  'alert-error-soft-ink': '#bf1a2f',
  'on-negative': '#ffffffde',
  'alert-warning-surface': '#efbd36',
  'on-warning': '#1f1f1f',
  'alert-warning-outline-ink': '#efbd36',
  'chip-active-bg': '#edfcf1',
  'chip-neutral-bg': '#fafbfc',
  'chip-negative-bg': '#fdf1f1',
  'table-row-hover': 'rgb(0 0 0 / 6%)',
  'table-sticky-head': '#fff',
  'table-sticky-cell': '#fafbfc',
  'image-thumb-surface': '#f5f5f5',
  'timecard-card-surface': '#fff',
  'chip-plain-surface': '#e0e0e0',
  'tab-panels-surface': '#fff',
  'schedule-surface': '#fff',
  'schedule-head': '#f5f5f5',
  'schedule-total': '#efefef',
  'schedule-stripe': 'rgb(0 0 0 / 5%)',
  'schedule-midnight': 'rgb(0 0 0 / 24%)',
  'page-background': '#fff',
  'positive-ink': '#4d833d',
  'chip-success-ink': '#2e7d46',
  'chip-info-ink': '#326ae4',
  'time-worked-note-ink': '#3f8b57',
  'time-worked-line-ink': '#5c7a66',
  'empty-note-ink': '#8b96a5',
  'tab-quiet-ink': '#9da0a0',
  'q-grey': '#9da0a0',
  'invite-hint-ink': '#9ca3af',
  'device-chip-ink': '#679f55',
  'device-chip-info-ink': '#326ae4',
  'shift-break-paid-ink': '#679f55',
  'shift-break-unpaid-ink': '#f49005',
  'voice-help-ink': '#cccdcb',
  'time-picker-background': '#fff',
  'time-picker-border': '#e8e8e8',
  'time-picker-text': '#73879c',
  'time-picker-text-hover': '#73879c',
  'time-picker-hover': '#f3f9fe',
  'time-picker-active': '#1284e7',
  'admin-topbar-background': '#ffeded',
  'admin-topbar-ink': '#f4261b',
};

/** Dark surfaces legacy defines only under `body--dark` (tokens.md §5 "dark surfaces"). */
export const backofficeDarkSurfaces = {
  dark: '#212224',
  'dark-page': '#101114',
  'dark-bg': '#1c1d20',
  'dark-bg-one': '#2a2b2e',
  '04dp': '#262629',
  '06dp': '#2a2b2e',
  '12dp': '#313235',
  '24dp': '#343537',
  'highlight-blue': '#414241',
} as const;

export const backofficeDark: BackofficeColors = {
  ...backofficeLight,
  'alert-info-surface': backofficeDarkSurfaces['highlight-blue'],
  'on-negative': '#1f1f1f',
  'alert-warning-surface': '#90762e',
  'on-warning': '#000000',
  'alert-warning-outline-ink': '#f2c03787',
  'q-primary': '#b4d5ac',
  'q-secondary': '#93d0ff',
  'q-accent': '#283446',
  'q-positive': '#b4d5ac',
  'q-negative': '#f19b9b',
  'q-info': '#31ccec87',
  'q-warning': '#f2c03787',
  'surface-lower': '#1c1d20',
  'field-surface': '#313235',
  'dialog-surface': '#212224',
  'menu-surface': backofficeDarkSurfaces['06dp'],
  'card-surface': backofficeDarkSurfaces.dark,
  'menu-ink': '#fff',
  'menu-ink-body': '#fff',
  'menu-item-hover': '#48494b',
  'sidebar-background': '#262629',
  'q-brand-primary': '#b4d5ac',
  'q-brand-secondary': '#93d0ff',
  'q-brand-primary-alternate': '#414241',
  'q-rad-medium': '#c0a3ac',
  'q-rad-alternate': 'rgb(255 255 255 / 50%)',
  'q-red-bg-select': '#414241',
  'q-text-primary': '#ffffffde',
  'q-text-secondary': '#ffffffb3',
  'q-text-tertiary': '#ffffff66',
  'close-glyph': '#ffffffde',
  'purple-button': '#c59afe',
  'purple-button-ink': '#000000',
  'orange-button': '#ffd497',
  'orange-button-ink': '#000000',
  'avatar-purple': '#c59afe',
  'avatar-orange': '#ffbc88',
  'avatar-green': '#b4d5ac',
  'avatar-blue': '#93d0ff',
  'avatar-gray': '#ffffffb3',
  'age-chip-blue': '#326ae4',
  'age-chip-blue-ink': '#326ae4',
  'age-chip-orange': '#f57c00',
  'age-chip-orange-ink': '#f57c00',
  'orange-tint-ink': '#ffbc88',
  'void-banner-orange': '#f57c00',
  'void-banner-orange-ink': '#f57c00',
  'count-ink': '#1c232e',
  'q-gray-50': '#2a2b2e',
  'q-border': '#373739',
  'q-brand-orange': '#ffbc88',
  'q-brand-orange-lighter': '#ffd4971a',
  'q-brand-orange-alternate-lighter': '#414241',
  'q-brand-cyan': '#b2ebf2',
  'q-brand-cyan-lighter': '#b2ebf21a',
  'q-brand-gold': '#ffe5a2',
  'q-brand-gold-lighter': '#ffe5a21a',
  'q-brand-purple': '#c59afe',
  'q-brand-purple-lighter': '#c59afe1a',
  'q-brand-green': '#b4d5ac',
  'q-brand-green-lighter': '#414241',
  'q-brand-blue': '#93d0ff',
  'q-brand-blue-lighter': '#414241',
  'q-brand-sidebar-active-indicator': '#b4d5ac',
  'q-surface-lift': '#2a2b2e',
  'q-surface-toggle-background': '#646866',
  'q-text-inverted': '#1c1d20',
  'q-light-yellow': '#fffbf666',
  // Legacy TableChipIndicator fills every chip transparent in the dark theme (`appStore.isDarkMode ? "transparent"`), the
  // inactive and negative ones too (B963: Next's kept their light fills, a pale blank pill).
  'chip-active-bg': 'transparent',
  'chip-neutral-bg': 'transparent',
  'chip-negative-bg': 'transparent',
  // Quasar's $table-dark-hover-background on legacy's dark q-tables (`.q-table--dark tbody td:before`), measured on Dining
  // Sales at 390 (B1214; Next had it transparent).
  'table-row-hover': 'rgb(255 255 255 / 7%)',
  'table-sticky-head': backofficeDarkSurfaces['06dp'],
  'table-sticky-cell': backofficeDarkSurfaces['06dp'],
  'image-thumb-surface': backofficeDarkSurfaces['24dp'],
  'timecard-card-surface': backofficeDarkSurfaces['04dp'],
  'chip-plain-surface': backofficeDarkSurfaces.dark,
  'tab-panels-surface': backofficeDarkSurfaces.dark,
  'schedule-surface': backofficeDarkSurfaces['dark-bg-one'],
  'schedule-head': backofficeDarkSurfaces['dark-bg'],
  'schedule-total': backofficeDarkSurfaces['12dp'],
  'schedule-stripe': 'rgb(255 255 255 / 6%)',
  'schedule-midnight': 'rgb(255 255 255 / 28%)',
  'page-background': backofficeDarkSurfaces['dark-page'],
  'positive-ink': '#b4d5ac',
  'chip-success-ink': '#2e7d46',
  'chip-info-ink': '#326ae4',
  'time-worked-note-ink': '#3f8b57',
  'time-worked-line-ink': '#5c7a66',
  'empty-note-ink': '#8b96a5',
  'tab-quiet-ink': '#9da0a0',
  'q-grey': '#9da0a0',
  'invite-hint-ink': '#9ca3af',
  // Legacy dark's --q-brand-green and --q-brand-blue (quasar.variables.scss) on its #414241 chip fills (6.3:1, 6.1:1).
  'device-chip-ink': '#b4d5ac',
  'device-chip-info-ink': '#93d0ff',
  // Legacy dark's --q-brand-green and --q-brand-orange on a schedule card.
  'shift-break-paid-ink': '#b4d5ac',
  'shift-break-unpaid-ink': '#ffbc88',
  'voice-help-ink': '#ffffff66',
  'time-picker-background': backofficeDarkSurfaces['04dp'],
  'time-picker-border': '#373739',
  'time-picker-text': '#ffffffde',
  'time-picker-active': '#b4d5ac',
  'admin-topbar-background': '#f4261bde',
  'admin-topbar-ink': '#ffeded',
};

/**
 * WCAG 2.2 AA contrast (ops 13-ui-parity-design-system.md "Accessibility"): legacy's tertiary gray #8c8d8c on white is
 * 3.4:1 and its green #4d833d on the green tints about 3.9:1, under the 4.5:1 AA needs for text this size. These
 * replacements keep the hue and darken just enough (5.2:1 and 5.4:1). On by default; `contrast: 'legacy'` restores the
 * legacy values for side-by-side parity captures. Recorded as an IMPROVE in the changes ledger.
 */
export const backofficeAaLight: Partial<BackofficeColors> = {
  // 4.6:1 on the True Margin Listed Price tint (q-brand-primary-alternate #e8f1e4), where a zero price is tertiary (B1429);
  // #6c6d6c had 4.48:1 there and failed the nightly journeys' axe check.
  'q-text-tertiary': '#6a6b6a',
  'q-brand-gray-dark': '#6c6d6c',
  'positive-ink': '#3c6a2f',
  // Account User Invite's button, white on legacy's #ad48e0 (4.4:1), darkened in the same hue to 4.6:1 (B1322).
  'purple-button': '#a846d9',
  // The location Invite button and Locations count, white on legacy's #f79206 (2.3:1), darkened to 4.6:1 (L143).
  'orange-button': '#b45f00',
  // ID Verification's age chips on a tenth of their colour: legacy's blue (4.2:1) and orange (2.45:1), darkened in the same
  // hues to 5.0:1 and 4.69:1 (B1645).
  'age-chip-blue-ink': '#2c5fd0',
  'age-chip-orange-ink': '#a85800',
  // The schedule's row avatars, white initials at 12 px on legacy's purple (4.1:1), orange (2.4:1) and green (3.2:1),
  // darkened in the same hues to 4.6:1; the blue (4.9:1) and dark grey (5.6:1) already pass.
  'avatar-purple': '#a44cd2',
  'avatar-orange': '#aa6403',
  'avatar-green': '#538045',
  // The order sheet's PAYMENT VOID, legacy's #f57c00 on a tenth of it (2.45:1) at 14 px bold, darkened in the same hue to
  // 4.69:1 (B1621); orange-button's #b45f00 reaches only 4.16:1 on this band.
  'void-banner-orange-ink': '#a85800',
  // Legacy's brand orange on its tenth (#fef4e6), 2.2:1, darkened in the same hue to 5.3:1; on the week grid's hatched
  // day (a 5% black stripe under the tint) it is 4.8:1, where #a85800 reached only 4.28:1 (B1664).
  'orange-tint-ink': '#9c5200',
  // Timecards' chips at 11 px: legacy's green on #e4f3ea (4.4:1) and blue on #eff3fd (4.4:1), darkened to 5.3:1 and 5.2:1.
  'chip-success-ink': '#27703e',
  'chip-info-ink': '#2c5fd0',
  // The timecard editor's Time Worked lines on #eaf5ee (3.7:1, 4.2:1) and the empty breaks note on #f3f4f6 (2.7:1).
  'time-worked-note-ink': '#2f6f45',
  'time-worked-line-ink': '#4c6a56',
  'empty-note-ink': '#5b6573',
  // A dense tab bar's unselected labels, legacy's grey on white (2.6:1), darkened in the same hue to 5.1:1 (B995).
  'tab-quiet-ink': '#6b6e6e',
  // Legacy's text-grey on white (2.6:1), darkened in the same hue to 5.1:1 as the dense tabs' labels (B999).
  'q-grey': '#6b6e6e',
  // The invite dialog's role hint, legacy's gray-400 on white (2.5:1), darkened to gray-500 (4.8:1).
  'invite-hint-ink': '#6b7280',
  // POS Devices' chips at 13 px bold: legacy's green on #e8f1e4 (2.7:1) and blue on #eff3fd (4.4:1), darkened in the same
  // hues to 4.7:1 and 5.2:1.
  'device-chip-ink': '#457638',
  'device-chip-info-ink': '#2c5fd0',
  // A schedule card's break counts at 11 px semibold: legacy's green and orange on the palest card tints (2.6:1, 2.0:1),
  // darkened in the same hues to 4.55:1 on every palette tint and the conflict fills (B1602).
  'shift-break-paid-ink': '#4b743e',
  'shift-break-unpaid-ink': '#9a5b03',
  // Voice Ordering's help lines, legacy's --q-border on white (1.6:1) at 12 px: the tertiary ink's 5.2:1 (B1162).
  'voice-help-ink': '#6c6d6c',
  // The time picker's gray items (3.7:1) and its blue chosen item (3.8:1) at 12 px.
  'time-picker-text': '#5e7086',
  'time-picker-text-hover': '#5e7086',
  'time-picker-active': '#0f6fc4',
  // The LOGGED IN AS bar's red on its pink (3.6:1) at 14 px: the same hue darkened to 4.6:1.
  'admin-topbar-ink': '#d41f14',
  // The outlined warning alert's text and glyph, legacy's --q-warning #efbd36 on white (1.7:1), darkened in the same hue to 5.0:1.
  'alert-warning-outline-ink': '#8c6a00',
};

/** Backoffice metrics in CSS px (tokens.md §5 and parity/backoffice/design-system.md §3–§6). */
export const backofficeMetrics = {
  font: { family: 'Roboto', body: 14, lineHeight: 1.5, letterSpacing: 0.25, tableHeader: 12, fieldLabel: 14, fieldMessage: 10, tabLabel: 15 },
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 48 },
  radius: { input: 8, button: 8, card: 8, dialog: 14, sidebar: 16, chip: 12, hubTile: 8 },
  table: { rowHeight: 44, stickyMaxHeight: 610, defaultRowsPerPage: 30 },
  field: { height: 56, denseHeight: 40, floatingLabelScale: 0.85 },
  button: { height: 40, mobileHeight: 48, minWidth: 120 },
  shell: { sidebarWidth: 250, toolbarMinHeight: 44, drawerBreakpoint: 1024, mobileBreakpoint: 768 },
  // Legacy BaseDialog asks for 740 px, but Quasar caps a dialog's card at 560 px from a 600 px viewport
  // (`.q-dialog__inner--minimized > div { max-width: 560px }`), so its dialogs draw 560 wide; only `max-width="lg"`
  // (modifier sets, a product's modifier overrides, logo and image upload, QR templates, the TM conflict) lifts the cap.
  dialog: { width: 560, wideWidth: 740, deleteWidth: 450, unsavedChangesWidth: 404 },
  toast: { errorMs: 800, successMs: 1000, warningMs: 4000 },
} as const;

export type BackofficeScheme = 'light' | 'dark';

export type BackofficeContrast = 'aa' | 'legacy';

export const backofficeColors = (scheme: BackofficeScheme, contrast: BackofficeContrast = 'aa'): BackofficeColors =>
  scheme === 'dark' ? backofficeDark : contrast === 'aa' ? { ...backofficeLight, ...backofficeAaLight } : backofficeLight;
